import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CRMUser, OpsCategory, OpsTask, Subtask, TaskPriority, TaskStatus } from '../../types';
import { Plus, Trash2, ClipboardList, RotateCcw } from 'lucide-react';
import { filterOpsTaskEmployeeTeam } from '../../utils/userRoles';
import {
  OPS_TASK_TEMPLATES,
  applyPersonNameToDraft,
  buildOpsTasksFromHiringTemplate,
  finalizeHiringDraft,
  hiringDraftToOpsTask,
  type BuiltOpsTaskDraft,
} from '../../utils/opsTaskTemplates';
import './OpsTemplatesPane.css';

export interface OpsTemplatesPaneProps {
  teamMembers: CRMUser[];
  onCreateTask: (data: Partial<OpsTask>) => Promise<string | undefined>;
  onOpenTask: (task: OpsTask) => void;
  onFinished: () => void;
}

interface PersonSlot {
  id: string;
  name: string;
}

interface SlotDraft {
  /** Token last written into the task (`{name}` or the person's name). */
  appliedName: string;
  draft: BuiltOpsTaskDraft;
}

function slotId(): string {
  return `p_${Math.random().toString(36).slice(2, 9)}`;
}

function parseDueDate(value: string): Date | undefined {
  if (!value) return undefined;
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
}

function toInputDate(d?: Date): string {
  if (!d) return '';
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}

function assigneesFromLines(subtasks: Subtask[]): string[] {
  return Array.from(new Set(subtasks.map((s) => s.assignedTo).filter((uid): uid is string => !!uid)));
}

function renumber(subtasks: Subtask[]): Subtask[] {
  return subtasks.map((item, index) => ({ ...item, position: index + 1 }));
}

const CATEGORY_OPTIONS: { value: OpsCategory; label: string }[] = [
  { value: 'admin', label: 'Admin' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'ordering', label: 'Ordering' },
  { value: 'requests', label: 'Requests' },
  { value: 'other', label: 'Other' },
];

const PRIORITY_OPTIONS: { value: TaskPriority; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

const STATUS_OPTIONS: { value: TaskStatus; label: string }[] = [
  { value: 'pending', label: 'Not Started' },
  { value: 'in-progress', label: 'In Progress' },
  { value: 'review', label: 'Review' },
  { value: 'completed', label: 'Completed' },
  { value: 'blocked', label: 'Blocked' },
  { value: 'on-hold', label: 'On Hold' },
];

const OpsTemplatesPane: React.FC<OpsTemplatesPaneProps> = ({
  teamMembers,
  onCreateTask,
  onOpenTask,
  onFinished,
}) => {
  const [templateId, setTemplateId] = useState<'hiring'>('hiring');
  const [people, setPeople] = useState<PersonSlot[]>(() => [{ id: slotId(), name: '' }]);
  const [slots, setSlots] = useState<Record<string, SlotDraft>>({});
  const dirtyRef = useRef<Set<string>>(new Set());
  const [dirtyIds, setDirtyIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const assignable = useMemo(() => filterOpsTaskEmployeeTeam(teamMembers), [teamMembers]);

  const freshDraft = (name: string): BuiltOpsTaskDraft | undefined => {
    const [built] = buildOpsTasksFromHiringTemplate({
      names: [name.trim() || '{name}'],
      users: teamMembers,
    });
    return built;
  };

  useEffect(() => {
    setSlots((prev) => {
      const next: Record<string, SlotDraft> = {};
      for (const person of people) {
        const token = person.name.trim() || '{name}';
        const current = prev[person.id];
        // Edited cards keep dates, assignees, and custom lines. The name still flows through.
        if (current && dirtyRef.current.has(person.id)) {
          next[person.id] = {
            appliedName: token,
            draft: applyPersonNameToDraft(current.draft, current.appliedName, token),
          };
        } else {
          const built = freshDraft(person.name);
          if (built) next[person.id] = { appliedName: token, draft: built };
        }
      }
      return next;
    });
    // Rebuild untouched drafts when the name or the team changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [people, teamMembers]);

  const markDirty = (id: string) => {
    if (dirtyRef.current.has(id)) return;
    dirtyRef.current.add(id);
    setDirtyIds(Array.from(dirtyRef.current));
  };

  const patchDraft = (id: string, patch: Partial<BuiltOpsTaskDraft>) => {
    markDirty(id);
    setSlots((prev) => {
      const current = prev[id];
      if (!current) return prev;
      const person = people.find((item) => item.id === id);
      const token = person?.name.trim() || current.appliedName;
      const base = person
        ? finalizeHiringDraft(current.draft, person.name, current.appliedName)
        : current.draft;
      return { ...prev, [id]: { appliedName: token, draft: { ...base, ...patch } } };
    });
  };

  const patchLines = (id: string, subtasks: Subtask[]) => {
    const lines = renumber(subtasks);
    patchDraft(id, { subtasks: lines, assignedTo: assigneesFromLines(lines) });
  };

  const resetDraft = (id: string, name: string) => {
    dirtyRef.current.delete(id);
    setDirtyIds(Array.from(dirtyRef.current));
    const built = freshDraft(name);
    if (!built) return;
    setSlots((prev) => ({
      ...prev,
      [id]: { appliedName: name.trim() || '{name}', draft: built },
    }));
  };

  const updateName = (id: string, value: string) => {
    const token = value.trim() || '{name}';
    setPeople((prev) => prev.map((person) => (person.id === id ? { ...person, name: value } : person)));
    // Same turn as the keystroke, so Create cannot save the {name} placeholder.
    setSlots((prev) => {
      const current = prev[id];
      if (!current) return prev;
      if (dirtyRef.current.has(id)) {
        return {
          ...prev,
          [id]: {
            appliedName: token,
            draft: applyPersonNameToDraft(current.draft, current.appliedName, token),
          },
        };
      }
      const built = freshDraft(value);
      if (!built) return prev;
      return { ...prev, [id]: { appliedName: token, draft: built } };
    });
  };

  const addPerson = () => setPeople((prev) => [...prev, { id: slotId(), name: '' }]);

  const removePerson = (id: string) => {
    dirtyRef.current.delete(id);
    setDirtyIds(Array.from(dirtyRef.current));
    setPeople((prev) => (prev.length <= 1 ? [{ id: slotId(), name: '' }] : prev.filter((person) => person.id !== id)));
  };

  const namedPeople = people.filter((person) => person.name.trim() && slots[person.id]);

  const handleCreate = async () => {
    if (namedPeople.length === 0 || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      let first: { id: string; draft: BuiltOpsTaskDraft } | null = null;
      for (const person of namedPeople) {
        const slot = slots[person.id];
        if (!slot) continue;
        const draft = finalizeHiringDraft(slot.draft, person.name, slot.appliedName);
        if (!draft.title.trim()) continue;
        const id = await onCreateTask(hiringDraftToOpsTask(draft));
        if (id && !first) first = { id, draft };
      }
      if (!first) {
        setError('Could not create the tasks.');
        return;
      }
      const now = new Date();
      onOpenTask({
        ...hiringDraftToOpsTask(first.draft),
        id: first.id,
        title: first.draft.title,
        category: first.draft.category,
        assignedTo: first.draft.assignedTo,
        status: first.draft.status,
        priority: first.draft.priority,
        createdAt: now,
        updatedAt: now,
        createdBy: '',
      });
      onFinished();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the tasks.');
    } finally {
      setSubmitting(false);
    }
  };

  const createLabel = submitting
    ? 'Creating…'
    : namedPeople.length <= 1
      ? 'Create task'
      : `Create ${namedPeople.length} tasks`;

  return (
    <div className="ops-templates">
      <aside className="ops-templates-catalog">
        <div className="ops-templates-catalog-label">Templates</div>
        {OPS_TASK_TEMPLATES.map((template) => (
          <button
            key={template.id}
            type="button"
            className={`ops-templates-card${templateId === template.id ? ' active' : ''}`}
            onClick={() => setTemplateId(template.id)}
          >
            <ClipboardList size={16} />
            <span>
              <span className="ops-templates-card-title">{template.label}</span>
              <span className="ops-templates-card-blurb">{template.blurb}</span>
            </span>
          </button>
        ))}
      </aside>

      {templateId === 'hiring' && (
        <div className="ops-templates-main pm-scroll">
          <h2 className="ops-templates-heading">Hiring checklist</h2>
          <p className="ops-templates-lead">
            One task per person. Edit the title, notes, dates, and every checklist line before you create it.
          </p>

          <div className="ops-templates-field">
            <span className="ops-templates-label">People</span>
            {people.map((person, index) => (
              <div key={person.id} className="ops-templates-name-row">
                <input
                  className="ops-templates-input"
                  value={person.name}
                  placeholder="Full name"
                  aria-label={`Person ${index + 1}`}
                  onChange={(e) => updateName(person.id, e.target.value)}
                />
                <button
                  type="button"
                  className="ops-templates-icon-btn"
                  onClick={() => removePerson(person.id)}
                  aria-label={`Remove person ${index + 1}`}
                  disabled={people.length === 1 && !person.name}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            <button type="button" className="ops-templates-add" onClick={addPerson}>
              <Plus size={14} /> Add person
            </button>
          </div>

          {people.map((person) => {
            const slot = slots[person.id];
            const draft = slot
              ? finalizeHiringDraft(slot.draft, person.name, slot.appliedName)
              : undefined;
            if (!draft) return null;
            const dirty = dirtyIds.includes(person.id);
            return (
              <article key={person.id} className="ops-templates-task">
                <div className="ops-templates-task-head">
                  <span className="ops-templates-label">{person.name.trim() || 'New hire'}</span>
                  {dirty && (
                    <button
                      type="button"
                      className="ops-templates-add"
                      onClick={() => resetDraft(person.id, person.name)}
                    >
                      <RotateCcw size={12} /> Reset from template
                    </button>
                  )}
                </div>

                <input
                  className="ops-templates-title"
                  value={draft.title}
                  aria-label="Task title"
                  onChange={(e) => patchDraft(person.id, { title: e.target.value })}
                />

                <textarea
                  className="ops-templates-note"
                  value={draft.description}
                  aria-label="Task description"
                  rows={4}
                  onChange={(e) => patchDraft(person.id, { description: e.target.value })}
                />

                <div className="ops-templates-meta">
                  <label>
                    <span className="ops-templates-label">Category</span>
                    <select
                      className="ops-templates-select"
                      value={draft.category}
                      onChange={(e) => patchDraft(person.id, { category: e.target.value as OpsCategory })}
                    >
                      {CATEGORY_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <span className="ops-templates-label">Priority</span>
                    <select
                      className="ops-templates-select"
                      value={draft.priority}
                      onChange={(e) => patchDraft(person.id, { priority: e.target.value as TaskPriority })}
                    >
                      {PRIORITY_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <span className="ops-templates-label">Status</span>
                    <select
                      className="ops-templates-select"
                      value={draft.status}
                      onChange={(e) => patchDraft(person.id, { status: e.target.value as TaskStatus })}
                    >
                      {STATUS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <span className="ops-templates-label">Start</span>
                    <input
                      type="date"
                      className="ops-templates-select"
                      value={toInputDate(draft.startDate)}
                      onChange={(e) => patchDraft(person.id, { startDate: parseDueDate(e.target.value) })}
                    />
                  </label>
                  <label>
                    <span className="ops-templates-label">Due</span>
                    <input
                      type="date"
                      className="ops-templates-select"
                      value={toInputDate(draft.dueDate)}
                      onChange={(e) => patchDraft(person.id, { dueDate: parseDueDate(e.target.value) })}
                    />
                  </label>
                </div>

                <div className="ops-templates-label">Checklist</div>
                <ul className="ops-templates-lines">
                  {draft.subtasks.map((item) => (
                    <li key={item.id}>
                      <input
                        className="ops-templates-line-title"
                        value={item.title}
                        aria-label="Checklist line"
                        onChange={(e) => patchLines(
                          person.id,
                          draft.subtasks.map((line) => (
                            line.id === item.id ? { ...line, title: e.target.value } : line
                          )),
                        )}
                      />
                      <select
                        className="ops-templates-select"
                        value={item.assignedTo || ''}
                        aria-label={`Assignee for ${item.title}`}
                        onChange={(e) => patchLines(
                          person.id,
                          draft.subtasks.map((line) => (
                            line.id === item.id
                              ? { ...line, assignedTo: e.target.value || undefined }
                              : line
                          )),
                        )}
                      >
                        <option value="">Unassigned</option>
                        {assignable.map((user) => (
                          <option key={user.uid} value={user.uid}>{user.displayName}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="ops-templates-icon-btn"
                        aria-label={`Remove ${item.title}`}
                        onClick={() => patchLines(person.id, draft.subtasks.filter((line) => line.id !== item.id))}
                      >
                        <Trash2 size={14} />
                      </button>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  className="ops-templates-add"
                  onClick={() => patchLines(person.id, [
                    ...draft.subtasks,
                    {
                      id: `hire_custom_${Date.now()}`,
                      title: '',
                      completed: false,
                      position: draft.subtasks.length + 1,
                    },
                  ])}
                >
                  <Plus size={14} /> Add checklist line
                </button>
              </article>
            );
          })}

          {error && <p className="ops-templates-error">{error}</p>}

          <button
            type="button"
            className="ops-new-btn"
            disabled={namedPeople.length === 0 || namedPeople.some((person) => !slots[person.id]?.draft.title.trim()) || submitting}
            onClick={() => void handleCreate()}
          >
            {createLabel}
          </button>
        </div>
      )}
    </div>
  );
};

export default OpsTemplatesPane;
